import { useEffect, useRef, useState } from "react";
import api, { API, formatApiError } from "../../lib/api";
import PageHeader from "../../components/PageHeader";
import { Button } from "../../components/ui/button";
import { Switch } from "../../components/ui/switch";
import { Textarea } from "../../components/ui/textarea";
import { Input } from "../../components/ui/input";
import { Mail, Upload, RotateCcw, Loader2, Save } from "lucide-react";
import { toast } from "sonner";

const BANNER_TYPES = [
  ["accesso", "OTP e accesso", "Codici OTP, credenziali, magic link e benvenuto Area Cliente, accessi demo"],
  ["messaggi", "Messaggistica", "Nuovi messaggi tra studio e cliente"],
  ["condivisioni", "Condivisione allegati", "Email con il link agli allegati condivisi"],
  ["notifiche", "Notifiche generali", "Nuovi documenti, upload del cliente, scadenze abbonamento e demo"],
];

const BannerPreview = ({ text, link, logoSrc }) => {
  let host = "";
  try { host = new URL(link).hostname; } catch { host = ""; }
  const i = host ? text.indexOf(host) : -1;
  const a = <a href={link} target="_blank" rel="noreferrer" className="text-blue-700 underline">{host}</a>;
  return (
    <div className="rounded-lg bg-white p-4" data-testid="email-banner-preview">
      <div className="flex items-center gap-3 border-t border-slate-200 pt-3">
        <img src={logoSrc} alt="" className="h-8 w-8 object-contain shrink-0" data-testid="email-banner-preview-logo" />
        <p className="text-xs leading-relaxed text-slate-500">
          {i >= 0 ? <>{text.slice(0, i)}{a}{text.slice(i + host.length)}</> : <>{text} {host && a}</>}
        </p>
      </div>
    </div>
  );
};

export default function EmailSettings() {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [bust, setBust] = useState(Date.now());
  const fileInput = useRef(null);

  const load = () => api.get("/admin/email-banner").then(({ data }) => { setCfg(data); setBust(Date.now()); })
    .catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  useEffect(() => { load(); }, []);

  const run = async (fn, ok) => {
    setBusy(true);
    try { await fn(); toast.success(ok); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const save = () => run(() => api.put("/admin/email-banner", { enabled: cfg.enabled, text: cfg.text, link_url: cfg.link_url, types: cfg.types }), "Impostazioni email salvate");
  const onPick = (e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    run(() => api.post("/admin/email-banner/logo", fd, { headers: { "Content-Type": "multipart/form-data" } }), "Logo aggiornato");
  };
  const resetLogo = () => run(() => api.delete("/admin/email-banner/logo"), "Ripristinato il logo PatroGestPro");
  const [notify, setNotify] = useState(null);
  useEffect(() => { api.get("/admin/notify-email").then(({ data }) => setNotify(data)).catch(() => {}); }, []);
  const saveNotify = async () => {
    try { const { data } = await api.put("/admin/notify-email", { email: notify.email }); setNotify(data); toast.success("Email notifiche salvata"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!cfg) return <div className="py-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-amber-500" /></div>;
  const logoSrc = `${API}/public/email-banner/logo?v=${bust}`;

  return (
    <div data-testid="admin-email-settings">
      <PageHeader title="Impostazioni Email" subtitle="Banner pubblicitario aggiunto in fondo a tutte le email automatiche inviate dal gestionale." icon={Mail} />
      {notify && (
        <div className="max-w-2xl mb-6 rounded-xl border border-slate-800 bg-[#121824] p-6 space-y-2" data-testid="sa-notify-card">
          <div className="text-sm font-semibold text-slate-100">Email per le notifiche al Superadmin</div>
          <div className="text-xs text-slate-400">Riceve le notifiche delle nuove richieste compagnie dagli studi.{!notify.email && notify.fallback ? ` Se vuoto si usa ${notify.fallback}.` : ""}</div>
          <div className="flex gap-2">
            <Input type="email" value={notify.email} onChange={(e) => setNotify({ ...notify, email: e.target.value })} placeholder={notify.fallback || "nome@dominio.it"}
              className="bg-slate-900/60 border-slate-700" data-testid="sa-notify-email-input" />
            <Button onClick={saveNotify} data-testid="sa-notify-save" className="bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold"><Save className="h-4 w-4 mr-2" /> Salva</Button>
          </div>
        </div>
      )}
      <div className="max-w-2xl rounded-xl border border-slate-800 bg-[#121824] p-6 space-y-6">
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="text-sm font-semibold text-slate-100">Banner in fondo alle email</div>
            <div className="text-xs text-slate-400" data-testid="email-banner-status">{cfg.enabled ? "Attivo nelle tipologie selezionate qui sotto" : "Disattivato"}</div>
          </div>
          <Switch checked={cfg.enabled} onCheckedChange={(v) => setCfg({ ...cfg, enabled: v })} data-testid="email-banner-enabled-switch" />
        </div>
        <div className="space-y-2" data-testid="email-banner-types">
          <label className="text-xs font-medium text-slate-300">Tipologie di email in cui mostrare il banner</label>
          <div className="rounded-lg border border-slate-700 divide-y divide-slate-800">
            {BANNER_TYPES.map(([k, label, hint]) => (
              <div key={k} className="flex items-center justify-between gap-4 px-4 py-3">
                <div className="min-w-0">
                  <div className="text-sm text-slate-100">{label}</div>
                  <div className="text-xs text-slate-400">{hint}</div>
                </div>
                <Switch checked={!!cfg.types?.[k]} disabled={!cfg.enabled} onCheckedChange={(v) => setCfg({ ...cfg, types: { ...cfg.types, [k]: v } })} data-testid={`email-banner-type-${k}`} />
              </div>
            ))}
          </div>
        </div>
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <label className="text-xs font-medium text-slate-300">Testo del banner</label>
            <button type="button" onClick={() => setCfg({ ...cfg, text: cfg.default_text, link_url: cfg.default_link })} data-testid="email-banner-reset-text"
              className="inline-flex items-center gap-1 text-xs text-amber-400 hover:text-amber-300 transition-colors"><RotateCcw className="h-3 w-3" /> Testo predefinito</button>
          </div>
          <Textarea value={cfg.text} onChange={(e) => setCfg({ ...cfg, text: e.target.value })} rows={3} maxLength={500}
            className="bg-slate-900/60 border-slate-700" data-testid="email-banner-text-input" />
        </div>
        <div className="space-y-2">
          <label className="text-xs font-medium text-slate-300">Link (il dominio presente nel testo diventa cliccabile)</label>
          <Input value={cfg.link_url} onChange={(e) => setCfg({ ...cfg, link_url: e.target.value })} className="bg-slate-900/60 border-slate-700" data-testid="email-banner-link-input" />
        </div>
        <div className="space-y-2">
          <label className="text-xs font-medium text-slate-300">Logo</label>
          <div className="flex flex-wrap items-center gap-2">
            <div className="h-14 w-14 rounded-lg border border-slate-700 bg-white flex items-center justify-center"><img src={logoSrc} alt="" className="h-10 w-10 object-contain" data-testid="email-banner-logo-img" /></div>
            <Button disabled={busy} onClick={() => fileInput.current?.click()} data-testid="email-banner-logo-upload" className="bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold"><Upload className="h-4 w-4 mr-2" /> Carica logo</Button>
            {cfg.has_custom_logo && <Button variant="outline" disabled={busy} onClick={resetLogo} data-testid="email-banner-logo-reset" className="border-slate-700"><RotateCcw className="h-4 w-4 mr-2" /> Logo PatroGestPro</Button>}
          </div>
          <input ref={fileInput} type="file" accept=".png,.jpg,.jpeg,.webp,.gif" className="hidden" onChange={onPick} data-testid="email-banner-logo-input" />
        </div>
        <div className="space-y-2">
          <label className="text-xs font-medium text-slate-300">Anteprima</label>
          <BannerPreview text={cfg.text} link={cfg.link_url} logoSrc={logoSrc} />
        </div>
        <Button onClick={save} disabled={busy} data-testid="email-banner-save" className="bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold">
          {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />} Salva
        </Button>
      </div>
    </div>
  );
}
